import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/Button/Button';
import { Card } from '../../components/Card/Card';
import { AngleLeft } from '../../icons/AngleLeft';
import './Planning.scss';
import { EventList } from './eventList/EventList';
import { AduitTrailAjaxResult } from '../../shared/types/types';
import { LoadingSpinner, largeSpinner } from '../../components/common/loader/LoadingSpinner';
import { v4 } from 'uuid';
import { WAS_BACK_END_API_URL, URL_VERSION } from 'src/constants';
import { decryptObject } from 'src/components/Encryption/obfuscationHandler';
import { fetchAuthenticated } from 'src/controllers';
import { BannerMessageContext } from 'src/shared/contexts/BannerMessageContext';
import { MessageList } from './messageList/MessageList';

export interface PlanningProps {
  token: string | null;
}

export function Planning({ token }: PlanningProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isEventExpanded, setIsEventExpanded] = useState(true);
  const [currentJob, setCurrentJob] = useState(null);

  const [planningListData, setPlanningList] = useState<any[]>([]);
  const [planningListDataError, setPlanningListDataError] = useState(false);
  const [planningListDataIsLoading, setPlanningListDataIsLoading] = useState(true);

  const { addBannerMessage } = useContext(BannerMessageContext);
  const url = `${WAS_BACK_END_API_URL}${URL_VERSION}/doseplanning`;
  // This applies only to the first request
  const getPlanningData = useCallback(async () => {
    const config: RequestInit = {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    };
    try {
      const response = await fetchAuthenticated<AduitTrailAjaxResult>(url, config);
      if (response?.ok && response?.parsedBody) {
        const decryptData = await decryptObject(response.parsedBody?.result, token, null);
        setPlanningListDataIsLoading(false);
        setPlanningList(decryptData.dosePlanningList);
      }
    } catch (error) {
      addBannerMessage({
        id: v4(),
        message: 'Uh oh! An error occurred downloading the Planning List!',
        type: 'error',
      });
      setPlanningListDataIsLoading(false);
      setPlanningList([]);
    }
  }, [url, token, addBannerMessage]);

  useEffect(() => {
    if (!planningListData || !planningListDataIsLoading) return;
    try {
      getPlanningData();
    } catch (e: any) {
      const error = new Error('Uh oh! An error occurred downloading the Planning List!', e);
      addBannerMessage({
        id: v4(),
        message: 'Uh oh! An error occurred downloading the Planning List!',
        type: 'error',
      });
      setPlanningListDataIsLoading(false);
      setPlanningListDataError(true);
      throw error;
    }
  }, [getPlanningData, planningListData, addBannerMessage, planningListDataIsLoading]);

  const handleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const handleEventExpanded = () => {
    setIsEventExpanded(!isEventExpanded);
  };

  const navigate = useNavigate();

  const handleEventClick = (eventId: string) => {
    for (let i = 0; i < planningListData.length; i++) {
      if (planningListData[i]?._id === eventId) {
        setCurrentJob(planningListData[i]);
      }
    }
  };

  const listError = <div className="error">There was an error loading the Planning Event List. Please refresh to try again.</div>;

  const body = (
    <div className="planning-content-container">
      <div className="event-list-container">
        <div className="section-header">event list</div>
        {planningListDataError ? (
          listError
        ) : planningListDataIsLoading ? (
          <div className="load-spinner">
            <LoadingSpinner width={largeSpinner} height={largeSpinner} />
          </div>
        ) : (
          <EventList eventList={planningListData} onEventClick={handleEventClick} />
        )}
      </div>
    </div>
  );

  const bodyDetails = (
    <div className="planning-content-container">
      <div className="event-list-container">
        <div className="section-header">messages list</div>
        <MessageList messageList={currentJob} />
      </div>
    </div>
  );

  return (
    <div className="planning-container" data-testid="Planning">
      <Card title={'Planning Events'} body={isExpanded ? body : <></>} onExpand={handleExpanded} expandable expanded={isExpanded} />
      <Card title={'Event Details'} body={isEventExpanded ? bodyDetails : <></>} onExpand={handleEventExpanded} expandable expanded={isEventExpanded} />

      <div className="page-controls">
        <Button
          label={'Back'}
          className={'go-back-button'}
          onClick={() => {
            navigate('/');
          }}
          icon={<AngleLeft />}
          iconPlacement={'left'}
        />
      </div>
    </div>
  );
}
